import { useEffect, useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import QuoteForm from '../../components/feature/QuoteForm';

const ContactPage = () => {
  useEffect(() => {
    document.title = 'Contact NWA Roofing | Free Quote | Fayetteville, Rogers, Bentonville AR';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact NWA Roofing for free roof inspection and quote. Call (479) 345-5312 or fill out our form. Serving Fayetteville, Springdale, Rogers, Bentonville, Bella Vista. Licensed, insured, A+ BBB rated roofing contractor.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'contact NWA roofing, roofing quote, free inspection, Fayetteville roofer contact');
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 bg-gradient-to-br from-orange-600 to-orange-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              Contact Us
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed px-2">
              Get connected with trusted roofing contractors in Northwest Arkansas. Free quotes, fast response, expert service.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6 sm:mb-8">Get In Touch</h2>
              
              <div className="space-y-6 sm:space-y-8 mb-8 sm:mb-12">
                <div className="flex items-start">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-orange-100 rounded-lg mr-4 sm:mr-6 flex-shrink-0">
                    <i className="ri-phone-line text-xl sm:text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Phone</h3>
                    <a href="tel:4795551234" className="text-base sm:text-lg text-orange-600 hover:text-orange-700 cursor-pointer">
                      (479) 555-1234
                    </a>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">24/7 Emergency Service Available</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-orange-100 rounded-lg mr-4 sm:mr-6 flex-shrink-0">
                    <i className="ri-mail-line text-xl sm:text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Email</h3>
                    <a href="mailto:info@nwaroofing.com" className="text-base sm:text-lg text-orange-600 hover:text-orange-700 cursor-pointer break-all">
                      info@nwaroofing.com
                    </a>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-orange-100 rounded-lg mr-4 sm:mr-6 flex-shrink-0">
                    <i className="ri-map-pin-line text-xl sm:text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Service Area</h3>
                    <p className="text-base sm:text-lg text-gray-700">Northwest Arkansas</p>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">
                      Fayetteville, Springdale, Rogers, Bentonville, Bella Vista & surrounding areas
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-orange-100 rounded-lg mr-4 sm:mr-6 flex-shrink-0">
                    <i className="ri-time-line text-xl sm:text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Business Hours</h3>
                    <div className="text-sm sm:text-base text-gray-700 space-y-1">
                      <p>Monday - Friday: 7:00 AM - 7:00 PM</p>
                      <p>Saturday: 8:00 AM - 5:00 PM</p>
                      <p>Sunday: Emergency Service Only</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Follow Us</h3>
                <div className="flex gap-3 sm:gap-4">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors cursor-pointer active:scale-95"
                    aria-label="Follow us on Facebook"
                  >
                    <i className="ri-facebook-fill text-xl sm:text-2xl text-orange-600"></i>
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors cursor-pointer active:scale-95"
                    aria-label="Follow us on Twitter"
                  >
                    <i className="ri-twitter-fill text-xl sm:text-2xl text-orange-600"></i>
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors cursor-pointer active:scale-95"
                    aria-label="Follow us on Instagram"
                  >
                    <i className="ri-instagram-fill text-xl sm:text-2xl text-orange-600"></i>
                  </a>
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors cursor-pointer active:scale-95"
                    aria-label="Follow us on LinkedIn"
                  >
                    <i className="ri-linkedin-fill text-xl sm:text-2xl text-orange-600"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">Our Service Area</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              Proudly serving Northwest Arkansas communities with quality roofing services
            </p>
          </div>
          
          <div className="rounded-2xl overflow-hidden shadow-2xl h-[300px] sm:h-[400px] md:h-[500px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d207936.8239!2d-94.2574!3d36.0822!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87c9696b8e05b0e5%3A0x5d7c8b8e8e8e8e8e!2sFayetteville%2C%20AR!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="NWA Roofing Service Area Map"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-orange-600 to-orange-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 px-2 leading-tight">
            Need Immediate Assistance?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 leading-relaxed px-2">
            Our team is standing by to connect you with qualified roofing contractors for emergency repairs, inspections, and installations.
          </p>
          <a
            href="tel:4795551234"
            className="inline-flex items-center justify-center bg-white text-orange-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-orange-50 active:scale-95 transition-all shadow-xl whitespace-nowrap cursor-pointer min-h-[48px]"
          >
            <i className="ri-phone-fill mr-2"></i>
            Call (479) 555-1234
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;